import React, { useState, useRef, useEffect } from 'react';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { ArrowLeft } from "lucide-react";
import { app, API_BASE_URL } from '../../firebase/firebaseConfig';
import ErrorModal from '../ui/ErrorModal';
import { ReactComponent as HigloLogo } from '../../assets/signin-visuals/higlo_logo.svg';

function VerifyCode({ email, onVerificationSuccess, onGoBack }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isHandlingSubmit, setIsHandlingSubmit] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    const headerElement = document.querySelector('h1');
    if (headerElement) {
      headerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleCodeChange = (index, value) => {
    // Handle iOS one-time-code autofill (full code pasted into first input)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      if (digits.length > 1) {
        const newCode = [...code];
        for (let i = 0; i < 6; i++) {
          newCode[i] = digits[i] || '';
        }
        setCode(newCode);
        // Focus the last filled input or the next empty one
        const nextEmptyIndex = newCode.findIndex(digit => digit === '');
        const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
        inputRefs.current[focusIndex]?.focus();
        return;
      }
      return;
    }

    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus to next input if a digit was entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace to go to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];

    for (let i = 0; i < 6; i++) {
      newCode[i] = pastedData[i] || '';
    }
    setCode(newCode);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(digit => digit === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  // Handle autofill from iOS one-time-code suggestion
  const handleAutoFill = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    if (value.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = value[i] || '';
      }
      setCode(newCode);

      // Focus the appropriate input
      const nextEmptyIndex = newCode.findIndex(digit => digit === '');
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsHandlingSubmit(true);

    const verificationCode = code.join('');
    if (verificationCode.length !== 6) {
      setErrorMessage('Please enter a 6-digit verification code.');
      setShowErrorModal(true);
      setIsHandlingSubmit(false);
      return;
    }

    // Send email and code to backend
    try {
      const response = await fetch(
        `${API_BASE_URL}/user/verify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, code: verificationCode }),
        }
      );
      // console.log(response);
      const data = await response.json();
      // console.log(data);
      if (data.success) {
        // Sign in with custom token
        const auth = getAuth(app);
        await signInWithCustomToken(auth, data.token);
        onVerificationSuccess();
      } else {
        if (data.error[0] == 4) {
          setErrorMessage("Incorrect code. Please check the code you've entered.")
        } else if (data.error[0] == 5) {
          setErrorMessage('Code has expired. Try signing in again.')
        } else {
          setErrorMessage('Verification failed. Please try again.');
        }
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsHandlingSubmit(false);
    }
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-md mx-auto space-y-8 pt-12">
        {/* Logo */}
        <div className="flex justify-center">
          <HigloLogo className="w-10 h-10" />
        </div>

        {/* Header */}
        <div>
          <h1 className="text-3xl font-title font-normal text-slate-900 text-center scroll-mt-1">Higlo Account Portal</h1>
          <p className="text-slate-600 text-center mt-3">
            Sign in to confirm shipping details, or if you already have your devices, to configure and manage them.
          </p>
        </div>

        {/* Verification Card */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Display */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Email address
                </label>
                <Input
                  type="email"
                  value={email}
                  disabled
                  className="pl-10 bg-slate-50"
                />
              </div>

              {/* Verification Code Input */}
              <div className="space-y-2">
                <label htmlFor="verificationCode" className="text-sm font-medium text-slate-700">
                  Verification Code
                </label>
                <p className="text-sm text-slate-500">
                  A verification code has been sent to your email. The code will expire in 10 minutes.
                </p>
                {/* Hidden input for iOS one-time-code autofill */}
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={handleAutoFill}
                  className="sr-only"
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <div className="flex gap-1 justify-center">
                  {code.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      maxLength={index === 0 ? undefined : 1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      className="w-10 h-10 text-center text-base font-semibold px-0 tracking-wider"
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full"
                  onClick={onGoBack}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={isHandlingSubmit}
                  className="w-full bg-gray-800 hover:bg-gray-700"
                >
                  {isHandlingSubmit ? "Signing in..." : "Sign In"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Error Modal */}
        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          title="Verification Error"
          onClose={closeErrorModal}
        />
      </div>
    </div>
  );
}

export default VerifyCode;
